import { Notification } from "@/utils/notificationUtils";
import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { agentApi } from "../../../modules/agent/api";
import { CreateUpdateAgentDirectorate } from "../../../modules/agent/types";
import { EnumerationType } from "../../../modules/enumerations/enums";
import type { UUID } from "../../../typings/global";
import { EntityObject, SearchPageRequest } from "../../types";
import { invalidateEnumeration } from "./enumerations";

export const AGENT_DIRECTORATES_QUERY_KEYS = {
  filterAgentDirectorates: (filter?: SearchPageRequest) =>
    filter ? ["filterAgentDirectorates", filter] : ["filterAgentDirectorates"],
  createAgentDirectorate: () => ["createAgentDirectorate"],
  updateAgentDirectorate: () => ["updateAgentDirectorate"],
  deleteAgentDirectorate: () => ["deleteAgentDirectorate"],
};

export const useFilterAgentDirectorates = (payload: SearchPageRequest) => {
  return useQuery({
    queryKey: AGENT_DIRECTORATES_QUERY_KEYS.filterAgentDirectorates(payload),
    queryFn: async () => {
      const { data } = await agentApi.filterAgentDirectorates(payload);

      return data;
    },
    placeholderData: keepPreviousData,
  });
};

export const useCreateAgentDirectorate = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: AGENT_DIRECTORATES_QUERY_KEYS.createAgentDirectorate(),
    meta: { globalSpinner: false },
    mutationFn: async (payload: CreateUpdateAgentDirectorate) => {
      const { data } = await agentApi.createAgentDirectorate(payload);

      return data;
    },
    onSuccess: () => {
      invalidateEnumeration(EnumerationType.AGENT_DIRECTORATES);
      queryClient.invalidateQueries({ queryKey: AGENT_DIRECTORATES_QUERY_KEYS.filterAgentDirectorates() });
      Notification.itemCreatedNotification();
    },
  });
};

export const useUpdateAgentDirectorate = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: AGENT_DIRECTORATES_QUERY_KEYS.updateAgentDirectorate(),
    meta: { globalSpinner: false },
    mutationFn: async (payload: EntityObject<CreateUpdateAgentDirectorate>) => {
      const { data } = await agentApi.updateAgentDirectorate(payload);

      return data;
    },
    onSuccess: () => {
      invalidateEnumeration(EnumerationType.AGENT_DIRECTORATES);
      queryClient.invalidateQueries({ queryKey: AGENT_DIRECTORATES_QUERY_KEYS.filterAgentDirectorates() });
      Notification.itemUpdatedNotification();
    },
  });
};

export const useDeleteAgentDirectorate = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: AGENT_DIRECTORATES_QUERY_KEYS.deleteAgentDirectorate(),
    mutationFn: (id: UUID) => {
      return agentApi.deleteAgentDirectorate({ id });
    },
    onSuccess: () => {
      invalidateEnumeration(EnumerationType.AGENT_DIRECTORATES);
      queryClient.invalidateQueries({ queryKey: AGENT_DIRECTORATES_QUERY_KEYS.filterAgentDirectorates() });
      Notification.itemDeletedNotification();
    },
  });
};
